import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, AfterViewInit, ViewEncapsulation } from '@angular/core';
import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { first, filter } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { LoginProvider, LoginRsp, UserRights } from './motes-login.models';
import { MotesLoginService, LoginOptions, LoginState } from './motes-login.service';
import { User } from '../models';
import { AutoSubOnDestroy, AutoSub } from '../providers/auto-sub';
import { CookieSection, MotesCookieService } from '../cookie/cookie.service';
import { UserService } from '../providers';

@AutoSubOnDestroy()
@Component({
    selector: 'motes-login',
    templateUrl: './motes-login.component.html',
    styleUrls: ['./motes-login.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [trigger('fadeInOut', [transition(':enter', [   // :enter is alias to 'void => *'
        style({ opacity: 0 }),
        animate(400, keyframes([
            style({ transform: "scale(.95)" }),
            style({ transform: "scale(1.03)" }),
            style({ transform: "scale(1)", opacity: 1 })
        ]))
    ])])],
    changeDetection: ChangeDetectionStrategy.Default
})

export class MotesLoginComponent implements AfterViewInit {

    public options: LoginOptions = this.loginService.options;
    public showLoginContainer: boolean = false;
    public error: string = '';
    public loading: string = '';
    public email: string = '';
    public password: string = '';
    public password2: string = '';
    public username: string = '';
    public timeSinceAdminRequest = 0;
    public adminRequestCreatedOn = Date.now();
    public adminRequestTimer = null;

    public latestLoginRsp: LoginRsp = null;

    LoginState = LoginState;
    CookieSection = CookieSection;

    public _state: LoginState = LoginState.SIGN_IN_SELECTION;
    private stateHistory: LoginState[] = [LoginState.SIGN_IN_SELECTION];
    private readonly LOGIN_TIMEOUT = 60000;

    @Input() set state(state: LoginState) {
        if(state == this._state) return;
        this.changeLoginState(state);
    }

    @Input() loginHeader: string = '';
    @Input() loginText: string = '';
    @Input() overlay: boolean = false;
    @Input() motesCard: boolean = true;
    @Input() isCookiesHidden: boolean = false;
    @Input() hasWhiteBackground: boolean = true;

    @Input() allowedProviders = {
        facebook: this.loginService.options.facebook || false,
        google: this.loginService.options.google || false,
        apple: this.loginService.options.apple || false,
        email: this.loginService.options.email || false,
        azure: this.loginService.options.azure || false,
    };

    @Input() set autoLogin(type: 'admin' | 'user' | 'false') {
        if(!type || type == "false") return;
        (type == 'admin') ? this.loginService.requestAdminLogin() : this.loginService.requestAdminLogin();
    }

    @Output() onError: EventEmitter<LoginRsp> = new EventEmitter();
    @Output() onSuccess: EventEmitter<LoginRsp> = new EventEmitter();
    @Output() onLoginRsp: EventEmitter<LoginRsp> = new EventEmitter();
    @Output() closeMe: EventEmitter<boolean> = new EventEmitter();
    @Output() viewLoaded: EventEmitter<void> = new EventEmitter();
    @Output() stateChange: EventEmitter<LoginState> = new EventEmitter();

    // Export to view
    public readonly LoginProvider = LoginProvider;

    constructor(private loginService: MotesLoginService, private cookieService: MotesCookieService, private userService: UserService) {
        // Watch login responses and emit to listners (shortcut for easy component access, ex. web-components)
        AutoSub(this).reg['emitLoginRspSub'] = this.loginService.watchLoginResponses().subscribe(loginRsp => {
            this.onLoginRsp.emit(loginRsp);
        });

        // Subscribe for login and user DB changes
        if(!this.options.enableAdminRequest) return;

        AutoSub(this).reg['loginRspSub'] = this.loginService.watchLoginResponses().subscribe(loginRsp => {
            console.log("Got login rsp ", loginRsp);
            this.latestLoginRsp = loginRsp;
            // If no userrights in rsp, go to login selection
            if(!loginRsp || loginRsp && !loginRsp.userRights) {
                this.loginService.loginOverlayState = LoginState.SIGN_IN_SELECTION;
                return;
            }
            // Create a admin request if not already created
            if(loginRsp && loginRsp.isNewUser) {
                console.log("Create admin request");
                this.loginService.userFactory.watchCurrentUser().pipe(filter(user => user != null), first()).toPromise().then(user => {
                    this.loginService.userFactory.createAdminRequest(user);
                });
            }
        });

        AutoSub(this).reg['userSub'] = this.loginService.userFactory.watchCurrentUser().subscribe((user: User) => {
            if(!user) {
                this.adminRequestCreatedOn = 0;
                return;
            }
            // Now try to refresh the admin token
            this.loginService.refreshUserToken().then((userRights: UserRights) => {
                if(userRights.isAdmin) this.changeLoginState(LoginState.VERIFICATION_DONE);
                console.log("Rights after refresh ", userRights);
            });

            if(user.isAdmin) return;

            // Expose admin request time to view
            this.adminRequestCreatedOn = user.adminRequestOn;

            if(user.adminRequestOn > 0) {
                // Update admin request timer
                this.startAdminRequestTimer(user.adminRequestOn);
            }
        });
    }

    public ngAfterViewInit() {
        this.viewLoaded.emit();
    }

    public async createUser(): Promise<void> {
        // Validate user
        this.error = null;
        if(!this.email) this.error = $localize`Please fill out email`;
        else if(!this.validateEmail(this.email)) this.error = $localize`Email is not valid`;
        else if(!this.password) this.error = $localize`Please fill out password`;
        else if(this.password != this.password2) this.error = $localize`Passwords do not match`;

        // Set username to email if none is given (which is okay for now)
        if(!this.username) this.username = this.email;

        if(this.error) return;

        this.loading = $localize`Creating a user for you...`;

        const loginRsp = await this.loginService.createUserWithEmail(this.email, this.password, this.username);
        if(!loginRsp || loginRsp.err) return this.handleError(loginRsp);

        return this.handleSuccess(loginRsp);
    }

    public validateEmail(email) {
        let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    }

    public async login(provider: LoginProvider) : Promise<void> {
        let loginRsp = null;
        // Reset error state
        this.error = null;

        switch(provider) {
            case LoginProvider.Google:
                this.loading = $localize`Waiting for login through Google...`;
                loginRsp = await this.withTimeout(this.loginService.loginGoogle());
                break;

            case LoginProvider.Apple:
                this.loading = $localize`Waiting for login through Apple...`;
                loginRsp = await this.withTimeout(this.loginService.loginApple());
                break;

            case LoginProvider.Facebook:
                this.loading = $localize`Waiting for login through Facebook...`;
                loginRsp = await this.withTimeout(this.loginService.loginFacebook());
                break;

            case LoginProvider.Email:
                this.loading = $localize`Waiting for login with StoryHunt account...`;
                loginRsp = await this.withTimeout(this.loginService.loginWithEmail(this.email, this.password));
                break;

            case LoginProvider.Azure:
                this.loading = $localize`Waiting for login through Azure account...`;
                loginRsp = await this.withTimeout(this.loginService.loginAzure());
                break;
        }

        // Report any error
        if(!loginRsp || loginRsp.err) return this.handleError(loginRsp);
        // Handle login success
        this.handleSuccess(loginRsp);
    }

    /**
     * Add timeout to login functions
     */
    private withTimeout(loginPromise: Promise<LoginRsp>, timeoutMs: number = this.LOGIN_TIMEOUT): Promise<LoginRsp> {
        const timeoutPromise = new Promise<LoginRsp>((resolve, _) =>
            setTimeout(() => {
                resolve({
                    msg: "Login timed out - please try again",
                    err: `Login timed out after ${this.LOGIN_TIMEOUT} ms`,
                    isNewUser: false,
                    userRights: null
                });
            }, timeoutMs)
        );
        return Promise.race([loginPromise, timeoutPromise]);
    }

    private closeOverlay(): void {
        this.loginService.showLoginOverlay = false;
        this.changeLoginState(LoginState.SIGN_IN_DONE);
        this.closeMe.emit(true);
    }

    public async loginSucceeded() {
        const user = await firstValueFrom(this.loginService.userFactory.watchCurrentUser());
        if(user.emailPermittedUpdatedOn) this.closeOverlay();
        else this.changeLoginState(LoginState.GET_EMAIL_PERMISSION);
    }

    public abortLogin() {
        this.loginService.registerLoginAbort();
        this.closeOverlay();
    }

    public recoverPassword(email: string): void {
        this.loading = $localize`Requesting password recover...`;
        this.loginService.recoverPassword(email).then((success) => {
            alert("An email has been sent to " + email + ' with a link to reset your password.');
            this.loading = '';
            this.changeLoginState(LoginState.SIGN_IN_WITH_EMAIL);
        }).catch((err) => {
            this.handleError({ msg: "Recovery error", err: err, isNewUser: false, userRights: null });
        });
    }

    public logout(): void {
        this.loginService.logout();
    }

    private handleSuccess(loginRsp: LoginRsp): void {
        this.error = null;
        this.loading = '';
        // Verify that user is admin if this login optin is enabled
        if(this.loginService.options.enableAdminRequest && loginRsp && loginRsp.userRights.isAnonymous) {
            this.changeLoginState(LoginState.ANONYMOUS_ADMIN_REQ);
        }
        else if(this.loginService.options.enableAdminRequest && !loginRsp.userRights.isAdmin && !loginRsp.err) {
            //if(this.options.enableAdminRequest) {
            // Wait for admin request to be fullfilled
            this.changeLoginState(LoginState.VERIFY_ADMIN);
            this.loginService.showLoginOverlay = true;
            return;
        }
        else {
            // Move on if no errors detected
            this.loginSucceeded();
        }

        this.onSuccess.emit(loginRsp);
    }

    public resendAdminRequest() {
        // Get current user, then resend admin request
        this.loginService.userFactory.watchCurrentUser().pipe(first()).toPromise().then((user: User) => {
            // Create a admin request if not already created
            this.loginService.userFactory.createAdminRequest(user);
        });
    }

    private handleError(loginRsp: LoginRsp): void {
        this.loading = '';
        //this.state = LoginState.ERROR;

        console.log("Login response warning: ", loginRsp);

        this.error = this.getProviderErrorMsg(loginRsp);
        this.onError.emit(loginRsp);
    }

    private getProviderErrorMsg(loginRsp: LoginRsp): string {
        if(!loginRsp?.err) {
            return $localize`Something went wrong. Please try again.`;
        }
        // Get error code from error message if it and instance of FirebaseError
        const errorCode = (typeof loginRsp.err == 'string') ? loginRsp.err : loginRsp.err.code;

        switch(errorCode) {
            case "auth/invalid-email":
            case "auth/user-not-found":
                return $localize`The email address does not seem to exist. Did you type it correctly?`;
            case "auth/wrong-password":
                return $localize`The password seem to be incorrect.`;
            case "auth/email-already-in-use":
                return $localize`Could not create new user - the email is already in use.`;
            case "auth/no-oidc-user":
                return $localize`The user is not registered correctly via OIDC. Please use another account or contact your administrator.`;

            default:
                return $localize`Something went wrong. Please try again.`;
        }
    }

    public changeLoginState(state: LoginState): void {
        // Set state
        this._state = state;
        // Push state to history (for navigating back)
        this.stateHistory.push(state);
        this.stateChange.emit(this._state);
        // Reset error 
        this.error = null;
    }

    public goBack() {
        // Delete last state from history to ensure
        this.stateHistory.splice(this.stateHistory.length - 1, 1);
        // Go back to last state 
        this._state = this.stateHistory[this.stateHistory.length >= 1 ? this.stateHistory.length - 1 : 0];
        this.stateChange.emit(this._state);
        // Clear error
        this.error = null;
    }

    public startAdminRequestTimer(requestTime: number) {
        if(this.adminRequestTimer) clearInterval(this.adminRequestTimer);
        this.adminRequestTimer = setInterval(() => {
            this.timeSinceAdminRequest = Date.now() - requestTime;
        }, 1000);
    }

    public openCookies(section: CookieSection): void {
        this.cookieService.showCookieInfo(true, section);
    }

    public async setEmailPermitted(isPermitted: boolean) {
        const now = this.userService.getServerTimestamp();
        const user = await this.loginService.userFactory.watchCurrentUser().pipe(first()).toPromise();
        user.isEmailPermitted = isPermitted;
        user.emailPermittedUpdatedOn = now;
        await this.userService.updateUser(user);
        this.closeOverlay();
    }
}
